import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useMemo } from "react";
import { useParams } from "react-router-dom";
import { FormGroup, Label } from "reactstrap";
import useConfig from "../../../../../actions/Tenants/config/configHook";
import { useRegisterDefectsMutation } from "../../../../../hooks/mutations";
import { checkQueryKey } from "../../../../../hooks/mutations/usePatchCheckMutation";
import { getFilledArrayOrDefault } from "../../../../../utils";
import MetaForm from "../../../../Forms/MetaForm";
import { doFilterDefects, useTenantDefectDefinitions } from "../../../../Forms/useManagedOptionsHook";
import { useQcEntities } from "../../QcEntityContext";
import usePatchAvocado from "../../usePatchAvocado";

export type FruitMetaFormProps = {
    avocado: any;
    fields: any[];
    grid?: boolean;
}

export const FruitMetaForm = ({ avocado, fields, grid }: FruitMetaFormProps) => {
    const config = useConfig();
    const queryClient = useQueryClient();
    const params = useParams();
    const tenantId = params.tenant_id as string;

    const { check, layer } = useQcEntities();

    const unitOfAccount = config?.get_check_unit_of_account(layer, check) || "Fruit";
    const patchAvocado = usePatchAvocado();
    const registerDefectsMutation = useRegisterDefectsMutation();
    const allDefects = useTenantDefectDefinitions(layer.fruit_type);

    // * Severity order from config (lightest to most severe)
    // * Falls back to enum values if nothing is configured
    const severityOrder = useMemo(() => {
        return config?.get_defect_severities()?.values || [];
    }, [config]);

    const severityOptions = useMemo(() => {
        return config?.get_defect_severities()?.options || [];
    }, [config]);

    const getSeverityOrder = useCallback((severity: string): number => {
        const index = severityOrder.indexOf(severity);
        return index === -1 ? 999 : index;
    }, [severityOrder]);

    const activeDefects: any[] = getFilledArrayOrDefault(avocado?.defects)
        .filter((i) => i.frequency > 0)
        .map((i) => {
            const tenantDefect = allDefects.find((d) => d.defect_id === i.defect_id);

            // * Set defect_key for easier filtering
            return { ...i, value: i.defect_id, defect_key: i.defect_id.replace(`${tenantDefect?.position}_${tenantDefect?.severity}_`, "") };
        });

    // * First filter the form for defect groups, so that we can define setValue and onDebounce functions
    const filterFormRound1 = (field, index) => {
        if (field.name !== "defects") {
            return field;
        }

        const options = doFilterDefects({
            defect_severity: field.defect_severity,
            defect_position: field.defect_position,
        }, allDefects || []);

        return {
            ...field,
            return_objects: true,
            type: "checkbox",
            multi: true,
            name: `defect_group_${index}`,
            options
        };
    };

    // * Apply filter 1: adjusts the fields for usage with defects so that the correct options are rendered
    // * By splitting al defect group in seperate fields
    const formFieldsAdjustedForDefects = fields.map(filterFormRound1);

    // * update the local/global state of the fruit
    const setValue = useCallback((fieldName, value) => {
        // * global states stores one list of defects
        if (fieldName && fieldName.startsWith("defect_group_")) {
            const field = formFieldsAdjustedForDefects.find((i) => i.name === fieldName);
            const group_defect_ids = field.options.map((i) => i.value);

            fieldName = "defects";
            // * remove all defects that are in this radio button group
            const activeDefectsWithoutTheseGroupDefects = activeDefects.filter((d) => !group_defect_ids.includes(d.defect_id));
            // * then only add the ones that are selected
            value = [...activeDefectsWithoutTheseGroupDefects, ...value.map((d) => ({ ...d, frequency: 1, defect_id: d.value }))];
        }

        patchAvocado(avocado, fieldName, value, false);
    }, [activeDefects]);

    // * make the api call to update the fruit
    const onDebounce = (fieldName, value) => {
        if (fieldName && fieldName.startsWith("defect_group_")) {
            const cachedCheck = queryClient.getQueryData<any>(checkQueryKey(check));
            const cachedFruit = cachedCheck?.avocados?.find((fruit) => fruit.avocado_id === avocado.avocado_id);
            const defectIds = getFilledArrayOrDefault(cachedFruit?.defects)
                .filter((defect) => defect.frequency > 0)
                .map((defect) => defect.defect_id);

            registerDefectsMutation.mutate({
                tenantId,
                checkId: avocado.check_id,
                data: {
                    fruit_id: avocado.avocado_id,
                    check_id: avocado.check_id,
                    defect_ids: defectIds,
                    positions: [],
                    severities: [],
                    unregister_others: true,
                },
            });
            return;
        }
        // API failures are already surfaced by the shared Axios interceptor.
        // Consume the mutation rejection so a failed autosave does not become
        // an uncaught runtime error that replaces the check form.
        patchAvocado(avocado, fieldName, value, true).catch(() => undefined);
    };


    // * split the avocado defects list into seperate groups
    const object = formFieldsAdjustedForDefects
        .filter((field) => field?.name?.startsWith("defect_group_"))
        .reduce((acc, field) => ({ ...acc, [field?.name]: activeDefects.filter((d) => field?.options?.map((i) => i.defect_id).includes(d.defect_id)) }), avocado);

    // * Filter the fields again, to enable the "defect_severity_on_one_line" option
    // * This filter happens as last because the `object`, `setValue` and `onDebounce` functions are needed
    const filterFormRound2 = (fruitFormField) => {
        // *  No alteration needed ?
        if (!fruitFormField?.name?.startsWith("defect_group_") || !fruitFormField?.defect_severity_on_one_line) {
            return fruitFormField;
        }

        const subObject = {};

        // * Set all options of the group per defect, but hide them by default
        const optionsWithDefectKey = fruitFormField.options.map((i) => ({ ...i, defect_key: i.value.replace(`${i.position}_${i.severity}_`, "") }));

        // * group the defects by defect_id
        const defects_grouped_by_defect = optionsWithDefectKey.reduce((acc, i) => {
            // * value is formatted as "{postion}_{severity}_{defect_id}"
            const { defect_key } = i; // e.g "sunburn"


            if (acc[defect_key]) {
                return acc;
            }
            // * Filter options belong to this defect key (e.g. "sunburn") and change label to severity (e.g. "Minor")
            // * Sort by severity order (from lightest to most severe)
            const defectKeyOptions = optionsWithDefectKey
                .filter((j) => j.defect_key === defect_key)
                .map((i) => ({ ...i, label: severityOptions.find((s) => s.value === i.severity)?.label ?? i.severity }))
                .sort((a, b) => getSeverityOrder(a.severity) - getSeverityOrder(b.severity));
            // * array of possible values
            const defectKeyValues = defectKeyOptions.map((i) => i.value);
            // * Find first selected option for this defect key
            const subObjectValue = activeDefects.find((i) => i.defect_key === defect_key);
            // * Set the value of the sub object for the meta form state
            subObject[defect_key] = subObjectValue?.value || null;

            return { ...acc,
                [defect_key]: {
                    ...i,
                    description: null,
                    type: "radio",
                    name: defect_key,
                    label: i.label,
                    onchange_callback: ({ value, setValue }) => {
                        // * Remove the all severity options for this defect key
                        const groupDefectsWithoutThisDefect = getFilledArrayOrDefault(object[fruitFormField.name]).filter((i) => !defectKeyValues.includes(i.value));
                        if (!value) {
                            // * deselect action
                            setValue(fruitFormField.name, groupDefectsWithoutThisDefect);
                            return;
                        }
                        // * Then add the only selectedseverity option
                        setValue(fruitFormField.name, [...groupDefectsWithoutThisDefect, { value, frequency: 1, defect_id: value }]);
                    },
                    options: defectKeyOptions
                }
            };
        }, {});

        const headerItem = {
            type: "header",
            label: fruitFormField.label,
        };

        // * Render nested form for the grouped defects
        return {
            ...fruitFormField,
            type: "element",
            el: <MetaForm
                setValue={setValue}
                onDebounce={onDebounce}
                debounce_time={500}
                grid={grid}
                config={config}
                meta={[headerItem, ...Object.values(defects_grouped_by_defect)]}
                object={subObject}
                extra_context={{ fruit_variety: layer.fruit_variety || undefined, fruit_type: layer.fruit_type || undefined }}
            />
        };
    };

    const formFieldsAdjustedForDefects2 = formFieldsAdjustedForDefects.map(filterFormRound2);

    let wrapperClass = "";
    if (grid) {
        // * if we display the fruit form in grid format (multiple fruit), then we add column with the fruit number
        const index = check.avocados.findIndex((i) => i.avocado_id === avocado.avocado_id);

        fields = [
            {
                type: "element",
                lg: "1",
                el: <FormGroup>
                    <Label>{unitOfAccount}</Label>
                    <div>{index + 1}</div>
                </FormGroup>
            },
            ...fields
        ];

        wrapperClass = index !== 0 ? "border-top pt-3" : "";
    }

    return (
        <div className={wrapperClass}>
            <MetaForm
                setValue={setValue}
                onDebounce={onDebounce}
                debounce_time={500}
                grid={grid}
                config={config}
                meta={formFieldsAdjustedForDefects2}
                object={object}
                extra_context={{ fruit_variety: layer.fruit_variety || undefined, fruit_type: layer.fruit_type || undefined }}
            />
        </div>
    );
};
